import { Box, Button, Skeleton, Typography, alpha, useTheme } from '@mui/material'
import React from 'react'
import { bookingStatusConfig } from './bookingStatusConfig'

export type BookingStatusVariant = 'failed' | 'pending' | 'refund'

interface BookingStatusProps {
    title: string
    subtitle: string
    primaryButtonText: string
    onPrimaryClick: (isReloadRequired: boolean) => void
    secondaryButtonText?: string
    onSecondaryClick?: () => void
    isLoading: boolean
    variant: BookingStatusVariant
}

const BookingStatus: React.FC<BookingStatusProps> = ({
    title,
    subtitle,
    primaryButtonText,
    onPrimaryClick,
    secondaryButtonText,
    onSecondaryClick,
    isLoading,
    variant
}) => {
    const config = bookingStatusConfig[variant]
    const IconComponent = config.icon
    const theme = useTheme()

    // Get background color based on variant
    const getBgColor = () => {
        if (variant === 'pending') {
            return alpha(theme.palette.info.main, 0.1)
        }
        if (variant === 'refund') {
            return alpha(theme.palette.warning.main, 0.1)
        }
        return config.bgColor
    }

    return (
        <Box
            sx={{
                marginTop: { xs: '150px', md: '250px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: { xs: 2, md: 3 },
                py: { xs: 4, md: 6 },
                bgcolor: 'background.default'
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                }}
            >
                <Box
                    sx={{
                        width: { xs: 60, md: 85 },
                        height: { xs: 60, md: 85 },
                        borderRadius: '50%',
                        bgcolor: getBgColor(),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3
                    }}
                >
                    <IconComponent
                        sx={{
                            fontSize: { xs: 40, md: 64 },
                            color: variant === 'pending' ? 'info.main' : undefined
                        }}
                        color={variant === 'pending' ? undefined : config.iconColor}
                    />
                </Box>

                <Typography
                    variant="h4"
                    sx={{
                        color: 'text.primary',
                        fontWeight: 600,
                        mb: 2,
                        fontSize: { xs: '1.5rem', md: '2.125rem' }
                    }}
                >
                    {title}
                </Typography>

                <Box
                    sx={{
                        mb: 3,
                        maxWidth: { xs: 320, md: 620 },
                        textAlign: 'center'
                    }}
                >
                    {!isLoading ? (
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: 14, md: 16 },
                                color: 'text.secondary',
                                lineHeight: 1.8,
                                mb: 0.5
                            }}
                        >
                            {subtitle}
                        </Typography>
                    ) : (
                        <Box display="flex" flexDirection="column" gap={1} alignItems="center">
                            <Skeleton
                                variant="rectangular"
                                sx={{
                                    width: { xs: 280, md: 580 }
                                }}
                            />
                            <Skeleton
                                variant="rectangular"
                                sx={{
                                    width: { xs: 300, md: 600 }
                                }}
                            />
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { sm: 'column', md: 'row' },
                        gap: 2,
                        mt: 2
                    }}
                >
                    {secondaryButtonText && onSecondaryClick && (
                        <Button
                            variant="outlined"
                            onClick={onSecondaryClick}
                            size="small"
                            sx={{
                                px: 4,
                                py: 1.5,
                                textTransform: 'none',
                                fontWeight: 600,
                                order: { xs: 2, sm: 1 }
                            }}
                        >
                            {secondaryButtonText}
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        onClick={() => onPrimaryClick(true)}
                        size="small"
                        sx={{
                            px: 4,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            order: { xs: 1, sm: 2 }
                        }}
                    >
                        {primaryButtonText}
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}

export { BookingStatus }
export type { BookingStatusProps }
